import { Head, Link } from "@inertiajs/react";
import { DoorOpen, Upload, Plus, Download, Award } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/EmptyState";
import { DataTable, Th, Td, rowClass } from "@/components/shared/DataTable";
import { formatRoom, formatGrade } from "@/lib/classroom";
import { formatMoney, formatNumber } from "@/lib/format";

export default function Index({ school, membership, campaign_name, classrooms, new_classroom_path, import_classrooms_path, all_sheets_path, all_certificates_path }) {
  const showDivision = classrooms.some((c) => c.division);

  return (
    <SchoolLayout
      school={school}
      membership={membership}
      eyebrow={campaign_name}
      title="Classrooms"
      actions={
        classrooms.length > 0 && (
          <>
            {import_classrooms_path && (
              <Button variant="outline" size="sm" asChild>
                <Link href={import_classrooms_path}><Upload className="h-4 w-4" /> Import a class list</Link>
              </Button>
            )}
            {new_classroom_path && (
              <Button variant="outline" size="sm" asChild>
                <Link href={new_classroom_path}><Plus className="h-4 w-4" /> New classroom</Link>
              </Button>
            )}
            {all_sheets_path && (
              <Button variant="outline" size="sm" asChild>
                <a href={all_sheets_path}><Download className="h-4 w-4" /> All take-home sheets</a>
              </Button>
            )}
            {all_certificates_path && (
              <Button variant="outline" size="sm" asChild>
                <a href={all_certificates_path}><Award className="h-4 w-4" /> All certificates</a>
              </Button>
            )}
          </>
        )
      }
    >
      <Head title="Classrooms" />

      {classrooms.length === 0 ? (
        <EmptyState
          icon={DoorOpen}
          title="No classrooms yet"
          description="Upload a class list to create your classrooms and students in one go, or add a classroom by hand."
          action={
            <div className="flex flex-wrap justify-center gap-2">
              {import_classrooms_path && (
                <Button asChild><Link href={import_classrooms_path}><Upload className="h-4 w-4" /> Import a class list</Link></Button>
              )}
              {new_classroom_path && (
                <Button variant="outline" asChild><Link href={new_classroom_path}><Plus className="h-4 w-4" /> New classroom</Link></Button>
              )}
            </div>
          }
        />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <Th>Classroom</Th>
              <Th>Grade</Th>
              {showDivision && <Th>Division</Th>}
              <Th align="right">Students</Th>
              <Th align="right">Raised</Th>
              <Th align="right">Minutes</Th>
              <Th>Teachers</Th>
            </tr>
          </thead>
          <tbody>
            {classrooms.map((c) => (
              <tr key={c.id} className={rowClass}>
                <Td><Link href={c.show_path} className="font-medium text-primary hover:underline">{formatRoom(c.name)}</Link></Td>
                <Td className="text-muted-foreground">{formatGrade(c.grade)}</Td>
                {showDivision && <Td className="text-muted-foreground">{c.division}</Td>}
                <Td align="right">{formatNumber(c.student_count)}</Td>
                <Td align="right" className="font-semibold text-foreground">{formatMoney(c.raised_cents)}</Td>
                <Td align="right" className="text-muted-foreground">{formatNumber(c.minutes_logged)}</Td>
                <Td className="text-muted-foreground">
                  {c.teachers.length > 0
                    ? c.teachers.join(", ")
                    : <Link href={c.edit_path} className="text-primary hover:underline">Add a teacher</Link>}
                </Td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </SchoolLayout>
  );
}
